import { Row, Col, Checkbox, Grid } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation, Trans } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Input as Input, Select, Modal } from "components";
import { localStorageHelper, returnBirthday } from "utils";
import { httpStatus } from "configs/httpStatus";

import {
  CreateUserActionCreators,
  SubDepartmentListActionCreators,
} from "redux/rootActions";
import { IGroupListOption } from "models/activities";
import { CreateUserData } from "models/createuserlist";
import { SubDepartmentListData } from "models/subdepartmentList";
import {
  createUserSuccessResponse,
  selectCreateUserErrorMessage,
} from "redux/createuserlist/createuserStates";
import { subDepartmentListDataResponse } from "redux/subdepartment/subdepartmentListStates";

import SuccessModal from "./SuccessModal";
import FailureModal from "./FailureModal";
import {
  Label,
  CompulsoryGroupLabel,
  ActivityGroupLabelContainer,
  ImportantStyled,
  SelectStyled,
  WapperActivity,
  CheckboxStyled,
  WapperError,
  WrapperBirthdayInput,
  BirthdayInput,
  DivActivity,
  ActivitiesStyle,
} from "./UserList.style";
import { IModal } from "./UserList.modal";

const { useBreakpoint } = Grid;

const schema = yup.object().shape({
  lastName: yup.string().trim().required("last-name-required"),
  firstName: yup.string().trim().required("first-name-required"),
  gender: yup.string().required("gender-required"),
  schoolYear: yup.string().required("school-year-required"),
  class: yup.string().required("class-required").nullable(),
  attendance: yup
    .number()
    .typeError("pls-enter-number")
    .nullable()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value
    ),
  activities: yup
    .mixed()
    .test(
      "check-activities",
      "activities-required",
      (currentActivities: any, context: any) => {
        const { activities, compulsoryGroup } = context.options.context;

        if (!compulsoryGroup) {
          return true;
        }

        const groups = compulsoryGroup.split(",");
        if (
          !currentActivities ||
          !currentActivities.length ||
          currentActivities.length < groups.length
        ) {
          return false;
        }
        const activitiesMapper: any = {};
        for (const activity of activities) {
          if (activity.children.length) {
            activitiesMapper[activity.text as any] = activity.children.map(
              (c: any) => c.id
            );
          } else {
            activitiesMapper[activity.text as any] = [activity.id];
          }
        }
        const activityValues = currentActivities.map((c: any) => c.id);
        for (const group of groups) {
          const children = activitiesMapper[group];
          const arrayIntersection = children.filter((value: any) =>
            activityValues.includes(value)
          );
          if (!arrayIntersection.length) return false;
        }
        return true;
      }
    ),
});

const CreatingModal = ({
  visible,
  handleCancel,
  handleSubmitOK,
  FooterModal,
  activityGroupOptions,
  departmentOptions,
  genderOptions,
}: IModal) => {
  const { t }: any = useTranslation("translation", { keyPrefix: "user-list" });

  const [activities, setActivities] = React.useState<IGroupListOption[]>();
  const [message, setMessage] = React.useState<any>("");
  const [activitySelectValue, setActivitySelectValue] = React.useState<any>([]);
  const [activityValue, setActivityValue] = React.useState<any>([]);
  const [groupNames, setGroupNames] = React.useState<any>([]);
  const [birthdayError, setBirthdayError] = React.useState<string>("");
  const {
    watch,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  }: any = useForm({
    reValidateMode: "onChange",
    defaultValues: {
      lastName: "",
      firstName: "",
      firstNameFurigana: "",
      lastNameFurigana: "",
      gender: undefined,
      birthYear: undefined,
      birthMonth: undefined,
      birthDay: undefined,
      attendance: "",
      schoolYear: undefined,
      class: undefined,
      activities: undefined,
    },
    resolver: yupResolver(schema),
    context: {
      compulsoryGroup: activityGroupOptions?.compulsory_group,
      activities,
    },
  });
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const dispatch = useDispatch();
  const subdepartmentOptions = useSelector(subDepartmentListDataResponse);
  const createUserSuccess = useSelector(createUserSuccessResponse);
  const createUserErrorMessage = useSelector(selectCreateUserErrorMessage);
  const watchSchoolYearField = watch("schoolYear");
  const [isVisibleMessage, setIsVisibleMessage] = useState({
    type: "",
    status: false,
  });
  const screens = useBreakpoint();

  useEffect(() => {
    setActivities(
      activityGroupOptions && activityGroupOptions?.group_list !== undefined
        ? activityGroupOptions?.group_list
        : []
    );
    if (activityGroupOptions?.compulsory_group) {
      setGroupNames(activityGroupOptions?.compulsory_group.split(","));
    }
    setBirthdayError("");
  }, []);

  useEffect(() => {
    if (watchSchoolYearField !== undefined) {
      const subDepartmentparams: SubDepartmentListData = {
        locale: currentLanguage,
        department_id: watchSchoolYearField,
        role: localStorageHelper.getItem("role"),
      };
      dispatch(
        SubDepartmentListActionCreators.subDepartmentListAction(
          subDepartmentparams
        )
      );
    }
  }, [watchSchoolYearField]);

  const onSubmit = (data: any, e: any) => {
    e.preventDefault();
    let dateOfBirth = "";
    if (!data.birthDay && !data.birthMonth && !data.birthYear) {
      dateOfBirth = "2000-01-01";
    } else if (data.birthDay && data.birthMonth && data.birthYear) {
      const birthMonth =
        data.birthMonth.length && data.birthMonth.length < 2
          ? `0${data.birthMonth}`
          : data.birthMonth;
      const birthDay =
        data.birthMonth.length && data.birthDay.length < 2
          ? `0${data.birthDay}`
          : data.birthDay;
      dateOfBirth = [data.birthYear, birthMonth, birthDay].join("-");
    } else {
      setBirthdayError("birthday-required");
      return;
    }
    const createUserParams: CreateUserData = {
      user: {
        first_name: data.firstName,
        last_name: data.lastName,
        furi_first_name: data.firstNameFurigana,
        furi_last_name: data.lastNameFurigana,
        gender: Number(data.gender),
        date_of_birth: dateOfBirth,
        dept_id: data.schoolYear,
        sub_dept_id: data.class,
        roll_number: data.attendance,
        company_sub_department_id: activityValue,
      },
      locale: currentLanguage,
    };
    if (
      !moment(dateOfBirth, "YYYY-MM-DD", true).isValid() ||
      moment(dateOfBirth).diff(moment(new Date())) > 0 ||
      moment(dateOfBirth).diff(moment("1900-01-01")) < 0
    ) {
      setBirthdayError("enter-correct-date");
      return;
    }
    dispatch(CreateUserActionCreators.createUserAction(createUserParams));
    setBirthdayError("");
  };

  useEffect(() => {
    if (createUserSuccess && Object.keys(createUserSuccess).length) {
      setMessage(createUserSuccess?.message);
      setIsVisibleMessage({ type: "success", status: true });
    }
    dispatch(CreateUserActionCreators.handleResetAction());
  }, [createUserSuccess]);

  useEffect(() => {
    if (createUserErrorMessage) {
      setMessage(createUserErrorMessage);
      setIsVisibleMessage({ type: "", status: true });
      dispatch(CreateUserActionCreators.handleResetAction());
    }
  }, [createUserErrorMessage]);

  const handleCheckbox = (value: any) => {
    if (value) {
      const temp: any = value.map((value: any) => {
        return value.text;
      });
      setActivitySelectValue(temp);
      const tempId: any = value.map((value: any) => {
        return value.id;
      });
      setActivityValue(tempId);
    }
  };
  const renderMessageModal = () => {
    if (isVisibleMessage.type === "success") {
      return (
        <SuccessModal
          visible={isVisibleMessage.status}
          message={message}
          handleCancel={handleCancel}
        />
      );
    }
    return (
      <FailureModal
        visible={isVisibleMessage.status}
        message={message}
        handleCancel={handleCancel}
      />
    );
  };
  return (
    <>
      {isVisibleMessage.status ? (
        renderMessageModal()
      ) : (
        <Modal
          type="form"
          title={t("user-registration")}
          visible={visible}
          onCancel={handleCancel}
          onOk={handleSubmit(onSubmit)}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row gutter={16}>
              <Col xs={12} md={12} xl={12}>
                <Controller
                  control={control}
                  name="lastName"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      value={value}
                      label={
                        <>
                          {t("last-name")}
                          <ImportantStyled>*</ImportantStyled>
                        </>
                      }
                      height={31}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      placeholder={"リーバー"}
                      onChange={onChange}
                      error={t(error?.message)}
                    />
                  )}
                />
              </Col>
              <Col xs={12} md={12} xl={12}>
                <Controller
                  control={control}
                  name="firstName"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      value={value}
                      label={
                        <>
                          {t("first-name")}
                          <ImportantStyled>*</ImportantStyled>
                        </>
                      }
                      height={31}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      placeholder={"太郎"}
                      onChange={onChange}
                      error={t(error?.message)}
                    />
                  )}
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={12} xl={12}>
                <Controller
                  control={control}
                  name="lastNameFurigana"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      value={value}
                      label={t("last-name-furigana")}
                      height={31}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      onChange={onChange}
                      error={t(error?.message)}
                      placeholder={"りーばー"}
                    />
                  )}
                />
              </Col>
              <Col xs={12} xl={12}>
                <Controller
                  control={control}
                  name="firstNameFurigana"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Input
                      value={value}
                      label={t("first-name-furigana")}
                      height={31}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      onChange={onChange}
                      error={t(error?.message)}
                      placeholder={"たろう"}
                    />
                  )}
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={12} md={8} xl={8}>
                <Controller
                  control={control}
                  name="gender"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Select
                      value={value}
                      label={
                        <>
                          {t("gender")}
                          <ImportantStyled>*</ImportantStyled>
                        </>
                      }
                      placeholder={t("please-select")}
                      options={genderOptions}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      onChange={onChange}
                      error={t(error?.message)}
                    />
                  )}
                />
              </Col>
              {!screens.sm && (
                <Col xs={12} md={8} xl={8}>
                  <Controller
                    control={control}
                    name="attendance"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Input
                        value={value}
                        label={t("attendance")}
                        height={31}
                        fsLabel={16}
                        marginForm="0 0 22px 6px"
                        onChange={onChange}
                        error={t(error?.message)}
                        placeholder={"1"}
                      />
                    )}
                  />
                </Col>
              )}
              <Col xs={24} md={8} xl={8}>
                <WrapperBirthdayInput>
                  <p>{t("birthday")}</p>
                  <BirthdayInput className="enter-year">
                    <Controller
                      control={control}
                      name="birthYear"
                      render={({ field: { onChange, value } }) => (
                        <Input
                          value={value}
                          height={31}
                          fsLabel={16}
                          onChange={onChange}
                          fs={16}
                          marginForm="0 0 22px 0"
                          placeholder={"2000"}
                          maxLength="4"
                        />
                      )}
                    />
                    <span>{t("birth-year")}</span>
                  </BirthdayInput>
                  <BirthdayInput>
                    <Controller
                      control={control}
                      name="birthMonth"
                      render={({ field: { onChange, value } }) => (
                        <Input
                          value={value}
                          height={31}
                          fsLabel={16}
                          onChange={onChange}
                          fs={16}
                          marginForm="0 0 22px 0"
                          placeholder={"01"}
                          maxLength="2"
                        />
                      )}
                    />
                    <span>{t("birth-month")}</span>
                  </BirthdayInput>
                  <BirthdayInput>
                    <Controller
                      control={control}
                      name="birthDay"
                      render={({ field: { onChange, value } }) => (
                        <Input
                          value={value}
                          height={31}
                          fsLabel={16}
                          onChange={onChange}
                          fs={16}
                          marginForm="0 0 22px 0"
                          placeholder={"01"}
                          maxLength="2"
                        />
                      )}
                    />
                    <span>{t("birth-day")}</span>
                  </BirthdayInput>
                  {birthdayError && (
                    <div className="birthday-error">{t(birthdayError)}</div>
                  )}
                </WrapperBirthdayInput>
              </Col>
              {screens.sm && (
                <Col xs={12} md={8} xl={8}>
                  <Controller
                    control={control}
                    name="attendance"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <Input
                        value={value}
                        label={t("attendance")}
                        height={31}
                        fsLabel={16}
                        marginForm="0 0 22px 6px"
                        onChange={onChange}
                        error={t(error?.message)}
                        placeholder={"1"}
                      />
                    )}
                  />
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12} xl={12}>
                <Controller
                  control={control}
                  name="schoolYear"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Select
                      value={value}
                      label={
                        <>
                          {t("school-year")}
                          <ImportantStyled>*</ImportantStyled>
                        </>
                      }
                      placeholder={t("please-select")}
                      options={departmentOptions}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      onChange={onChange}
                      error={t(error?.message)}
                    />
                  )}
                />
              </Col>
              <Col xs={24} md={12} xl={12}>
                <Controller
                  control={control}
                  name="class"
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Select
                      value={value}
                      label={
                        <>
                          {t("class")}
                          <ImportantStyled>*</ImportantStyled>
                        </>
                      }
                      placeholder={t("please-select")}
                      options={subdepartmentOptions?.result}
                      fsLabel={16}
                      fs={16}
                      marginForm="0 0 22px 0"
                      onChange={onChange}
                      error={t(error?.message)}
                    />
                  )}
                />
              </Col>
            </Row>
            <ActivityGroupLabelContainer>
              <Label>
                {t("activities")}
                {activityGroupOptions?.compulsory_group && (
                  <ImportantStyled>*</ImportantStyled>
                )}
              </Label>
              {activityGroupOptions?.compulsory_group ? (
                <CompulsoryGroupLabel>
                  <Trans i18nKey="user-list.select-group-required">
                    <strong>
                      「
                      {{
                        requiredGroup: activityGroupOptions?.compulsory_group,
                      }}
                      」
                    </strong>{" "}
                    の選択は必須です
                  </Trans>
                </CompulsoryGroupLabel>
              ) : (
                ""
              )}
            </ActivityGroupLabelContainer>
            <Controller
              control={control}
              name="activities"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div style={{ position: "relative" }}>
                  <SelectStyled
                    value={activitySelectValue}
                    mode="multiple"
                    marginForm="0 0 24px"
                    fs={14}
                    fsLabel={16}
                    height={"62px"}
                    placeholder={t("click-to-select-activities")}
                    showSearch={false}
                    dropdownRender={() => (
                      <Checkbox.Group
                        onChange={(value) => {
                          onChange(value);
                          handleCheckbox(value);
                        }}
                      >
                        <ActivitiesStyle>
                          <DivActivity>
                            {activities &&
                              activities.length > 0 &&
                              activities.map((item: any, index: number) => {
                                return (
                                  <WapperActivity key={index}>
                                    {item.children.length ? (
                                      <p>
                                        {item.text}
                                        {groupNames.indexOf(item.text) > -1 && (
                                          <ImportantStyled>*</ImportantStyled>
                                        )}
                                      </p>
                                    ) : (
                                      <CheckboxStyled value={item}>
                                        <p>
                                          {item.text}
                                          {groupNames.indexOf(item.text) >
                                            -1 && (
                                            <ImportantStyled>*</ImportantStyled>
                                          )}
                                        </p>
                                      </CheckboxStyled>
                                    )}

                                    {item.children.map(
                                      (a: any, index1: number) => {
                                        return (
                                          <div
                                            style={{
                                              marginBottom:
                                                index1 <
                                                item.children.length - 1
                                                  ? "6px"
                                                  : "0px",
                                            }}
                                            key={a.id}
                                          >
                                            <CheckboxStyled value={a}>
                                              {a.text}
                                            </CheckboxStyled>
                                          </div>
                                        );
                                      }
                                    )}
                                  </WapperActivity>
                                );
                              })}
                          </DivActivity>
                        </ActivitiesStyle>
                      </Checkbox.Group>
                    )}
                  />
                  {error?.message ? (
                    <WapperError>
                      <p>
                        <CompulsoryGroupLabel
                          style={{
                            marginBottom: 0,
                            marginLeft: 0,
                            lineHeight: "16px",
                          }}
                        >
                          <Trans i18nKey="user-list.activities-required">
                            <strong>
                              「
                              {{
                                requiredGroup:
                                  activityGroupOptions?.compulsory_group,
                              }}
                              」
                            </strong>{" "}
                            を選択してください
                          </Trans>
                        </CompulsoryGroupLabel>
                      </p>
                    </WapperError>
                  ) : undefined}
                </div>
              )}
            />
            {FooterModal(
              "#FFFFFF",
              "#E0E0E0",
              "#2AC769",
              "11px 24px 11px",
              t("cancel"),
              t("register"),
              handleCancel,
              handleSubmitOK
            )}
          </form>
        </Modal>
      )}
    </>
  );
};

export default CreatingModal;
